import { SearchOutlined } from '@ant-design/icons';
import { Alert, Divider, Input, Space, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import { ValueScanner } from '@/components/ValueScanner';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import { messages } from '@/features/scan/messages';
import {
  createDraftInputFromMeter,
  createDraftInputFromUnmatchedIdentifier,
  createPassportWizardDraftInputFromMeter,
} from '@/features/scan/scan-draft-inputs';
import { useManualMeterSearch } from '@/features/scan/useManualMeterSearch';
import type { CreateInventorizationDraftInput } from '@/features/today/useCreateInventorizationDraft';
import { type PendingScanMatch, ScanMatchModal, ScanSearchOutcome } from '@/routers/scan/components/ScanSearchOutcome';
import { VALUE_SCAN_PURPOSES } from '@/shared/camera/value-scanner';
import { METER_IDENTIFIER_KINDS } from '@/shared/meter-identification';

interface ScanViewProps {
  onCreateDraft: (input?: CreateInventorizationDraftInput) => Promise<unknown>;
  onOpenQueuedMeter: (sessionId: string) => void;
}

const manualSearchDebounceMs = 500;

export function ScanView({ onCreateDraft, onOpenQueuedMeter }: ScanViewProps) {
  const intl = useIntl();
  const { activeLocation } = useStartupSession();
  const { bootstrap } = useLocationBootstrap();
  const [rawValue, setRawValue] = useState('');
  const [scannedValue, setScannedValue] = useState<string | null>(null);
  const [pendingMatch, setPendingMatch] = useState<PendingScanMatch | null>(null);
  const { error, hasSearched, identifier, isSearching, otherLocationResults, queuedMatches, reset, results, search } =
    useManualMeterSearch({
      backendMeters: bootstrap?.existingMeters,
      locationId: activeLocation?.locationId,
    });
  const handleResolvedValue = useCallback((candidate: { rawValue?: string; value: string }) => {
    const value = candidate.rawValue?.trim() || candidate.value;

    setRawValue(value);
    setScannedValue(value);
  }, []);

  useEffect(() => {
    const value = rawValue.trim();

    if (!value) {
      reset();
      return;
    }

    const timeout = window.setTimeout(() => {
      void search(value);
    }, manualSearchDebounceMs);

    return () => window.clearTimeout(timeout);
  }, [rawValue, reset, search]);

  function handleRawValueChange(value: string) {
    setRawValue(value);
    setScannedValue(current =>
      normalizeComparableValue(current) === normalizeComparableValue(value) ? current : null,
    );
    reset();
  }

  async function handlePrimaryAction() {
    const [queuedMatch] = queuedMatches;
    const [meterMatch] = results;

    if (identifier && queuedMatch) {
      setPendingMatch({ identifier, session: queuedMatch, type: 'queued' });
      return;
    }

    if (identifier && meterMatch) {
      setPendingMatch({ identifier, meter: meterMatch, type: 'cached' });
      return;
    }

    if (hasSearched && identifier?.normalizedValue) {
      await handleContinueWithValue();
    }
  }

  async function handleContinueWithValue() {
    if (!identifier) {
      return;
    }

    await onCreateDraft(createDraftInputFromUnmatchedIdentifier(identifier));
  }

  async function handleConfirmMatch(match: PendingScanMatch) {
    if (match.type === 'queued') {
      onOpenQueuedMeter(match.session.id);
      return;
    }

    await onCreateDraft(
      createDraftInputFromMeter(match.identifier, match.meter, isCurrentValueFromScanner(rawValue, scannedValue)),
    );
  }

  async function handleRunPassport(match: Extract<PendingScanMatch, { type: 'cached' }>) {
    await onCreateDraft(
      createPassportWizardDraftInputFromMeter(
        match.identifier,
        match.meter,
        isCurrentValueFromScanner(rawValue, scannedValue),
      ),
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <ValueScanner
        autoAcceptHighConfidence
        compact
        onAccept={handleResolvedValue}
        purpose={VALUE_SCAN_PURPOSES.meterLookup}
        showCandidates={false}
        title={intl.formatMessage(messages.scanTitle)}
      >
        <Divider style={{ margin: 0 }} />
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Typography.Text type="secondary">{intl.formatMessage(messages.scanDescription)}</Typography.Text>
          <Input
            autoComplete="off"
            onChange={event => handleRawValueChange(event.target.value)}
            placeholder={intl.formatMessage(messages.scanPlaceholder)}
            prefix={<SearchOutlined />}
            size="large"
            value={rawValue}
          />
          <ScanSearchOutcome
            hasSearched={hasSearched}
            identifier={identifier}
            isSearching={isSearching}
            onCreate={() => void handleContinueWithValue()}
            onSkip={() => void onCreateDraft()}
            onUpdate={() => void handlePrimaryAction()}
            queuedMatches={queuedMatches}
            results={results}
          />
        </Space>
      </ValueScanner>

      {error ? <Alert closable message={error} showIcon type="warning" /> : null}
      {identifier?.kind === METER_IDENTIFIER_KINDS.apiKey && otherLocationResults.length > 0 ? (
        <Alert closable message={intl.formatMessage(messages.scanOtherLocationWarning)} showIcon type="warning" />
      ) : null}

      <ScanMatchModal
        match={pendingMatch}
        onCancel={() => setPendingMatch(null)}
        onConfirm={match => void handleConfirmMatch(match)}
        onRunPassport={match => void handleRunPassport(match)}
      />
    </Space>
  );
}

function isCurrentValueFromScanner(rawValue: string, scannedValue: string | null) {
  return Boolean(scannedValue && normalizeComparableValue(rawValue) === normalizeComparableValue(scannedValue));
}

function normalizeComparableValue(value: string | null) {
  return value?.trim() ?? '';
}
